import React, { useState } from 'react';
import { RemoteAlertGroup } from '../types/remoteAlerts';
import { useGetRemoteAlertGroupsQuery, useDeleteRemoteAlertGroupMutation } from '../services/dataApi';
import RemoteAlertGroupTable from '../components/remotealerts/RemoteAlertGroupTable';
import RemoteAlertGroupDialog from '../components/remotealerts/RemoteAlertGroupDialog';
import { Mail, Smartphone, Users, Bell } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';


const STEPS = [
  {
    icon: Bell,
    title: 'Set thresholds',
    desc: 'Go to Thresholds and configure high/low alarm values for each sensor, then enable the alarm.',
    color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
  },
  {
    icon: Users,
    title: 'Create an alert group',
    desc: 'Add a group below. Give it a name, assign the sensors to watch, and add the users who should be notified.',
    color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
  },
  {
    icon: Mail,
    title: 'Choose delivery',
    desc: 'For each recipient choose Email and/or SMS. Email is sent via TS1 Cloud; SMS goes via the TextMagic gateway.',
    color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
  },
  {
    icon: Smartphone,
    title: 'Alarm fires',
    desc: 'When a sensor breaches its threshold for the configured delay period, every recipient in the matching group is notified.',
    color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
  },
];

const RemoteAlertsPage: React.FC = () => {
  const { isAdmin } = usePermissions();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<RemoteAlertGroup | undefined>(undefined);
  const { data: alertGroups = [], isLoading, isError, refetch } = useGetRemoteAlertGroupsQuery();
  const [deleteRemoteAlertGroup] = useDeleteRemoteAlertGroupMutation();

  const handleOpenAddDialog = () => {
    setEditingGroup(undefined);
    setIsDialogOpen(true);
  };

  const handleOpenEditDialog = (group: RemoteAlertGroup) => {
    setEditingGroup(group);
    setIsDialogOpen(true);
  };

  const handleSaveGroup = () => {
    setIsDialogOpen(false);
    setEditingGroup(undefined);
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (window.confirm('Are you sure you want to delete this alert group?')) {
      try {
        // Optimistic remove in the mutation clears the row immediately; errors
        // invalidate the list cache so RTK reloads authoritative data.
        await deleteRemoteAlertGroup(Number(groupId)).unwrap();
      } catch (e) {
        alert('Failed to delete alert group.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-app-canvas dark:bg-gray-900 min-w-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-8 min-w-0 max-w-full">

        <div className="mb-5 md:mb-7">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Remote Alert Groups</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Send email and SMS notifications to your team when a sensor alarm activates.
          </p>
        </div>

        {/* How it works */}
        <div className="bg-app-panel dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-4">How it works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((step, i) => (
              <div key={i} className="flex gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${step.color}`}>
                  <step.icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 mb-0.5">Step {i + 1}</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white leading-snug mb-1">{step.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-700/50 rounded-lg px-4 py-3 mb-6 text-sm text-amber-800 dark:text-amber-300">
          <strong>Note:</strong> SMS requires users to have a mobile number saved on their account (set in the Users page). Alarm notifications respect the <em>Repeat</em> and <em>Delay</em> settings configured in Thresholds. External repeat email and SMS notifications cannot resend more often than once every 15 minutes, even when <em>Delay</em> is set lower.
        </div>

        {isLoading && <div className="text-gray-500 dark:text-gray-400 text-sm">Loading alert groups…</div>}
        {!isLoading && isError && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 mb-6 text-sm text-amber-900 dark:text-amber-100">
            <p className="font-medium mb-1">We couldn&apos;t load your alert groups this time.</p>
            <p className="text-amber-800/95 dark:text-amber-100/85 mb-2 leading-relaxed">
              If your account was just created, notification tables sometimes finish provisioning a little later.&nbsp;
              You can still use <strong>Add Group</strong> below — saving usually works once permissions are ready.
            </p>
            <button
              type="button"
              className="text-sm font-medium text-amber-900 dark:text-amber-100 underline hover:no-underline"
              onClick={() => refetch()}
            >
              Try again
            </button>
          </div>
        )}
        {!isLoading && (
          <RemoteAlertGroupTable
            alertGroups={(isError ? [] : alertGroups).map((group: any) => ({
              ...group,
              sensorIds: Array.isArray(group.sensors) ? group.sensors : (Array.isArray(group.sensorIds) ? group.sensorIds : []),
              recipients: Array.isArray(group.recipients) ? group.recipients : [],
            }))}
            onAddGroup={handleOpenAddDialog}
            onEditGroup={handleOpenEditDialog}
            onDeleteGroup={handleDeleteGroup}
            canManage={isAdmin}
          />
        )}
        {isDialogOpen && (
          <RemoteAlertGroupDialog
            group={editingGroup}
            onClose={() => setIsDialogOpen(false)}
            onSave={handleSaveGroup}
          />
        )}
      </div>
    </div>
  );
};

export default RemoteAlertsPage;
